// Role-based nav permissions and helpers
export const ROLE_LABELS = {
    admin: 'Administrator',
    sales: 'Sales Team',
    care: 'Customer Care',
    installation: 'Installation Team',
    vendor: 'Vendor',
    saathi: 'Solar Saathi',
    customer: 'Consumer',
};

export const STAFF_ROLES = ['admin', 'sales', 'care', 'installation', 'vendor', 'saathi'];

// Which nav keys are visible per role
export const ROLE_NAV = {
    admin: ['dashboard', 'reports', 'leads', 'quotations', 'sale-invoices', 'payment-ins', 'payment-center', 'cash-register', 'applications', 'projects', 'credit-reports', 'credit-report', 'bbps', 'bbps-reports', 'wallet', 'my-wallet', 'loans', 'eko-settings', 'redpay-settings', 'provider-router', 'complaints', 'saathi-bookings', 'vendors', 'vendor-ledger', 'vendor-coverage', 'customer-ledger', 'customers', 'pending-signups', 'staff', 'access-bundles', 'hierarchy', 'my-team', 'vendor-team', 'territory-heatmap', 'attendance', 'attendance-summary', 'staff-tracking', 'products', 'payments-report', 'login-events', 'push-console', 'broadcast-news', 'referrals', 'subsidy', 'subsidy-tracker', 'landing-cms', 'site-pages', 'blog', 'customer-stories', 'team', 'content', 'career-applications', 'notifications', 'view-messages', 'whatsapp-inbox', 'nerochat-logs', 'razorpay', 'mobile-app', 'deletion-requests', 'action-view', 'system-errors'],
    sales: ['dashboard', 'reports', 'leads', 'quotations', 'sale-invoices', 'customers', 'projects', 'credit-report', 'loans', 'saathi-bookings', 'my-team', 'my-wallet', 'referrals', 'my-attendance', 'my-attendance-history', 'deletion-requests', 'action-view'],
    care: ['dashboard', 'reports', 'leads', 'quotations', 'sale-invoices', 'payment-ins', 'payments-report', 'payment-center', 'cash-register', 'complaints', 'applications', 'projects', 'credit-report', 'bbps', 'wallet', 'my-wallet', 'loans', 'saathi-bookings', 'customers', 'vendor-ledger', 'customer-ledger', 'career-applications', 'my-team', 'my-attendance', 'my-attendance-history', 'deletion-requests', 'action-view'],
    installation: ['dashboard', 'reports', 'saathi-bookings', 'applications', 'projects', 'credit-report', 'loans', 'complaints', 'my-team', 'my-wallet', 'my-attendance', 'my-attendance-history', 'deletion-requests', 'action-view'],
    vendor: ['dashboard', 'reports', 'leads', 'quotations', 'sale-invoices', 'payment-ins', 'payments-report', 'customer-ledger', 'my-khata', 'applications', 'projects', 'credit-report', 'loans', 'saathi-bookings', 'complaints', 'customers', 'products', 'vendor-team', 'my-firm', 'my-wallet', 'my-attendance', 'my-attendance-history', 'deletion-requests', 'action-view'],
    // Solar Saathi = field agent who books site surveys, meets customers, handles leads & referrals
    saathi: ['dashboard', 'reports', 'leads', 'saathi-bookings', 'customers', 'credit-report', 'loans', 'referrals', 'my-team', 'my-wallet', 'my-attendance', 'my-attendance-history', 'deletion-requests', 'action-view'],
};

export function allowedNavFor(userOrRole) {
    // Accept either the whole user object (with permissions array) or a role string
    if (userOrRole && typeof userOrRole === 'object') {
        // Admin ALWAYS gets the full admin nav — their per-user permissions
        // array (if legacy/stale) must never hide admin-level modules like
        // Vendor Khata Book, Access Bundles, etc.
        if (userOrRole.role === 'admin') {
            return ROLE_NAV.admin;
        }
        if (Array.isArray(userOrRole.permissions) && userOrRole.permissions.length > 0) {
            // Always allow dashboard baseline; ensure 'my-attendance' is available to staff
            const perms = new Set(userOrRole.permissions);
            perms.add('dashboard');
            if (['sales', 'care', 'installation', 'vendor', 'saathi'].includes(userOrRole.role)) perms.add('my-attendance');
            // Phase 51 — Vendor role gets a floor of core financial +
            // self-service keys, so features shipped AFTER an existing
            // vendor's permissions array was baked (e.g. Payment-Ins,
            // Payments Report, Sale Invoices, My Firm / My Wallet) never
            // silently disappear because the DB row is stale.
            if (userOrRole.role === 'vendor') {
                [
                    'payment-ins', 'payments-report',
                    'quotations', 'sale-invoices',
                    'customer-ledger', 'my-khata',
                    'my-firm', 'my-wallet',
                    'my-attendance-history',
                    'action-view', 'deletion-requests',
                ].forEach(k => perms.add(k));
            }
            // Phase 51 — Same floor for Care team. Legacy Care users saved
            // a `permissions` array before Vendor Khata Book, Payment-Ins,
            // Vendor Coverage, etc. shipped. Without this floor those
            // sidebar items silently vanish for existing Care staff.
            if (userOrRole.role === 'care' || userOrRole.role === 'customercare') {
                [
                    'payment-ins', 'payments-report',
                    'vendor-ledger', 'vendor-coverage', 'customer-ledger',
                    'quotations', 'sale-invoices',
                    'payment-center', 'cash-register',
                    'complaints', 'applications',
                    'my-wallet', 'my-attendance-history',
                    'action-view', 'deletion-requests',
                ].forEach(k => perms.add(k));
            }
            return Array.from(perms);
        }
        return ROLE_NAV[userOrRole.role] || [];
    }
    return ROLE_NAV[userOrRole] || [];
}

// All possible nav keys with human labels for the staff permissions picker
export const ALL_PERMISSIONS = [
    { key: 'dashboard', label: 'Dashboard' },
    { key: 'reports', label: 'Reports (Hub)' },
    { key: 'leads', label: 'Leads' },
    { key: 'quotations', label: 'Quotations' },
    { key: 'sale-invoices', label: 'Sale Invoices' },
    { key: 'payment-ins', label: 'Payment-In / Receipts' },
    { key: 'payment-center', label: 'Payment Center' },
    { key: 'applications', label: 'Applications' },
    { key: 'projects', label: 'Projects' },
    { key: 'credit-reports', label: 'Credit Reports (Bureau)' },
    { key: 'bbps', label: 'BBPS Bill Pay' },
    { key: 'wallet', label: 'Wallet & Ledger (Admin — all users)' },
    { key: 'my-wallet', label: 'My Wallet (per-user)' },
    { key: 'site-surveys', label: 'Site Surveys (legacy — use "Visits & Surveys")' },
    { key: 'complaints', label: 'Complaints' },
    { key: 'saathi-bookings', label: 'Survey Visits' },
    { key: 'vendors', label: 'Vendors' },
    { key: 'customers', label: 'Customers' },
    { key: 'staff', label: 'Staff & Roles' },
    { key: 'attendance', label: 'Attendance Approval' },
    { key: 'attendance-summary', label: 'Attendance Report' },
    { key: 'staff-tracking', label: 'Live Staff Tracking' },
    { key: 'products', label: 'Product Catalog' },
    { key: 'payments-report', label: 'Payments Report' },
    { key: 'vendor-ledger', label: 'Vendor Khata Book' },
    { key: 'vendor-coverage', label: 'Vendor Coverage Map' },
    { key: 'customer-ledger', label: 'Customer Khata Book' },
    { key: 'my-khata', label: 'My Khata Book (Vendor self-view, read-only)' },
    { key: 'login-events', label: 'Login & Security' },
    { key: 'push-console', label: 'Push Console' },
    { key: 'broadcast-news', label: 'Broadcast News' },
    { key: 'hierarchy', label: 'User Hierarchy' },
    { key: 'my-team', label: 'My Team' },
    { key: 'vendor-team', label: 'Vendor Team' },
    { key: 'my-firm', label: 'My Firm Details' },
    { key: 'vendor-wallet', label: 'My Wallet (Vendor)' },
    { key: 'territory-heatmap', label: 'Territory Heatmap' },
    { key: 'my-attendance-history', label: 'My Attendance History' },
    { key: 'my-attendance', label: 'My Attendance' },
    { key: 'referrals', label: 'Referrals' },
    { key: 'subsidy', label: 'Subsidy Rates' },
    { key: 'subsidy-tracker', label: 'Subsidy Tracker' },
    { key: 'content', label: 'Content' },
    { key: 'landing-cms', label: 'Homepage CMS' },
    { key: 'site-pages', label: 'Website Pages CMS' },
    { key: 'blog', label: 'Blog / Knowledge Hub' },
    { key: 'customer-stories', label: 'Customer Stories' },
    { key: 'team', label: 'Our Team (Website)' },
    { key: 'notifications', label: 'Notifications' },
    { key: 'view-messages', label: 'View Messages (Outbox)' },
    { key: 'whatsapp-inbox', label: 'WhatsApp Inbox' },
    { key: 'nerochat-logs', label: 'Nerochat API Logs' },
    { key: 'mobile-app', label: 'Mobile App' },
    { key: 'deletion-requests', label: 'Deletion Requests' },
    { key: 'action-view', label: 'Action View' },
    { key: 'system-errors', label: 'System Errors' },
];
